import type {
  CarrierCode,
  CountryCallingCode,
  CountryCode,
  E164Number,
  NationalNumber,
  NumberType,
} from "libphonenumber-js";
import * as React from "react";
import parsePhoneNumberFromString, { AsYouType } from "libphonenumber-js";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@workspace/web-ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@workspace/web-ui/components/command";
import { Input } from "@workspace/web-ui/components/input";
import { Popover, PopoverContent, PopoverTrigger } from "@workspace/web-ui/components/popover";
import { ScrollArea } from "@workspace/web-ui/components/scroll-area";
import { cn } from "@workspace/web-ui/lib/utils";

import { countries } from "./countries";
import { useStateHistory } from "./use-state-history";

/**
 * Maximum length for international phone numbers according to E.164 standard
 * 15 digits + 1 for the '+' prefix = 16 characters
 */
const MAX_PHONE_NUMBER_LENGTH = 16;

export type Country = (typeof countries)[number];

export type PhoneData = {
  phoneNumber?: E164Number;
  countryCode?: CountryCode;
  countryCallingCode?: CountryCallingCode;
  carrierCode?: CarrierCode;
  nationalNumber?: NationalNumber;
  internationalNumber?: string;
  possibleCountries?: string;
  isValid?: boolean;
  isPossible?: boolean;
  uri?: string;
  type?: NumberType;
};

interface PhoneInputProps extends React.ComponentPropsWithoutRef<"input"> {
  value?: string;
  defaultCountry?: CountryCode;
}

export function getPhoneData(phone: string): PhoneData {
  const asYouType = new AsYouType();
  asYouType.input(phone);
  const number = asYouType.getNumber();
  return {
    phoneNumber: number?.number,
    countryCode: number?.country,
    countryCallingCode: number?.countryCallingCode,
    carrierCode: number?.carrierCode,
    nationalNumber: number?.nationalNumber,
    internationalNumber: number?.formatInternational(),
    possibleCountries: number?.getPossibleCountries().join(", "),
    isValid: number?.isValid(),
    isPossible: number?.isPossible(),
    uri: number?.getURI(),
    type: number?.getType(),
  };
}

function USFlag({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="14"
      viewBox="0 0 20 14"
      className={className}
    >
      <rect width="20" height="14" fill="#fff" />
      <g>
        <rect y="0" width="20" height="2" fill="#B22234" />
        <rect y="2" width="20" height="2" fill="#fff" />
        <rect y="4" width="20" height="2" fill="#B22234" />
        <rect y="6" width="20" height="2" fill="#fff" />
        <rect y="8" width="20" height="2" fill="#B22234" />
        <rect y="10" width="20" height="2" fill="#fff" />
        <rect y="12" width="20" height="2" fill="#B22234" />
      </g>
      <rect x="0" y="0" width="8" height="7" fill="#3C3B6E" />
      <g fill="#fff">
        <polygon points="1.2,1.3 1.36,1.81 1.87,1.81 1.46,2.09 1.62,2.59 1.2,2.28 0.78,2.59 0.93,2.09 0.53,1.81 1.04,1.81" />
        <polygon
          transform="translate(2,0)"
          points="1.2,1.3 1.36,1.81 1.87,1.81 1.46,2.09 1.62,2.59 1.2,2.28 0.78,2.59 0.93,2.09 0.53,1.81 1.04,1.81"
        />
        <polygon
          transform="translate(4,0)"
          points="1.2,1.3 1.36,1.81 1.87,1.81 1.46,2.09 1.62,2.59 1.2,2.28 0.78,2.59 0.93,2.09 0.53,1.81 1.04,1.81"
        />
        <polygon
          transform="translate(6,0)"
          points="1.2,1.3 1.36,1.81 1.87,1.81 1.46,2.09 1.62,2.59 1.2,2.28 0.78,2.59 0.93,2.09 0.53,1.81 1.04,1.81"
        />
        <polygon
          transform="translate(1,1.5)"
          points="1.2,1.3 1.36,1.81 1.87,1.81 1.46,2.09 1.62,2.59 1.2,2.28 0.78,2.59 0.93,2.09 0.53,1.81 1.04,1.81"
        />
        <polygon
          transform="translate(3,1.5)"
          points="1.2,1.3 1.36,1.81 1.87,1.81 1.46,2.09 1.62,2.59 1.2,2.28 0.78,2.59 0.93,2.09 0.53,1.81 1.04,1.81"
        />
        <polygon
          transform="translate(5,1.5)"
          points="1.2,1.3 1.36,1.81 1.87,1.81 1.46,2.09 1.62,2.59 1.2,2.28 0.78,2.59 0.93,2.09 0.53,1.81 1.04,1.81"
        />
      </g>
    </svg>
  );
}

export function PhoneInput({
  value: valueProp,
  defaultCountry = "US",
  className,
  id,
  required = true,
  ...rest
}: PhoneInputProps) {
  const asYouType = new AsYouType();

  const inputRef = React.useRef<HTMLInputElement>(null);

  const [value, handlers, history] = useStateHistory(valueProp);

  if (value && value.length > 0) {
    defaultCountry = parsePhoneNumberFromString(value)?.getPossibleCountries()[0] || defaultCountry;
  }

  const [openCommand, setOpenCommand] = React.useState(false);
  const [countryCode, setCountryCode] = React.useState<CountryCode>(defaultCountry);

  const selectedCountry = countries.find((country) => country.iso2 === countryCode);

  const initializeDefaultValue = () => {
    if (value) {
      return value;
    }

    return `+${selectedCountry?.phone_code}`;
  };

  const handleOnInput = (event: React.FormEvent<HTMLInputElement>) => {
    asYouType.reset();

    let value = event.currentTarget.value;
    if (!value.startsWith("+")) {
      value = `+${value}`;
    }

    // Remove all non-digit characters except the leading +
    const digitsOnly = value.replace(/[^\d+]/g, "");

    // Prevent input if it exceeds the maximum allowed length
    if (digitsOnly.length > MAX_PHONE_NUMBER_LENGTH) {
      return;
    }

    const formattedValue = asYouType.input(value);
    const number = asYouType.getNumber();
    setCountryCode(number?.country || defaultCountry);
    event.currentTarget.value = formattedValue;
    handlers.set(formattedValue);
  };

  const handleOnPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    asYouType.reset();

    const clipboardData = event.clipboardData;

    if (clipboardData) {
      const pastedData = clipboardData.getData("text/plain");

      // Remove all non-digit characters except the leading +
      const digitsOnly = pastedData.replace(/[^\d+]/g, "");

      // Prevent paste if it exceeds the maximum allowed length
      if (digitsOnly.length > MAX_PHONE_NUMBER_LENGTH) {
        return;
      }

      const formattedValue = asYouType.input(pastedData);
      const number = asYouType.getNumber();
      setCountryCode(number?.country || defaultCountry);
      event.currentTarget.value = formattedValue;
      handlers.set(formattedValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "z") {
      handlers.back();
      if (
        inputRef.current &&
        history.current > 0 &&
        history.history[history.current - 1] !== undefined
      ) {
        event.preventDefault();
        inputRef.current.value = history.history[history.current - 1] || "";
      }
    }
  };

  return (
    <div className={cn("flex gap-2", className)}>
      <Popover open={openCommand} onOpenChange={setOpenCommand} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={openCommand}
            className="w-max items-center justify-between whitespace-nowrap rounded-[8px]"
          >
            {selectedCountry?.name ? (
              <span className="flex items-center">
                {selectedCountry.iso2 === "US" ? (
                  <USFlag className="mr-2 h-4 w-6 object-cover" />
                ) : (
                  <img
                    src={`https://flagcdn.com/${selectedCountry.iso2.toLowerCase()}.svg`}
                    className="mr-2 h-4 w-6 object-cover"
                    alt={selectedCountry.name}
                  />
                )}
                +{selectedCountry.phone_code}
              </span>
            ) : (
              "Select country"
            )}
            <ChevronsUpDown className="ml-4 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-max p-0" align="start">
          <Command>
            <CommandInput placeholder="Search country..." />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <ScrollArea className={"[&>[data-radix-scroll-area-viewport]]:max-h-[300px]"}>
                <CommandGroup>
                  {countries.map((country) => {
                    return (
                      <CommandItem
                        key={country.iso3}
                        value={`${country.name} (+${country.phone_code})`}
                        onSelect={() => {
                          if (inputRef.current) {
                            inputRef.current.value = `+${country.phone_code}`;
                            handlers.set(`+${country.phone_code}`);
                            inputRef.current.focus();
                          }
                          setCountryCode(country.iso2 as CountryCode);
                          setOpenCommand(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 size-4",
                            countryCode === country.iso2 ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {country.iso2 === "US" ? (
                          <USFlag className="relative top-0.5 mr-2 h-3 w-4 object-cover" />
                        ) : (
                          <img
                            src={`https://flagcdn.com/${country.iso2.toLowerCase()}.svg`}
                            className="relative top-0.5 mr-2 h-3 w-4 object-cover"
                            aria-labelledby={country.name}
                            title={country.name}
                            alt={country.name}
                          />
                        )}
                        {country.name}
                        <span className="text-gray-11 ml-1">(+{country.phone_code})</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input
        ref={inputRef}
        type="text"
        pattern="^(\+)?[0-9\s]*$"
        name="phone"
        id={id}
        placeholder="Phone"
        defaultValue={initializeDefaultValue()}
        onInput={handleOnInput}
        onPaste={handleOnPaste}
        onKeyDown={handleKeyDown}
        required={required}
        aria-required={required}
        {...rest}
      />
    </div>
  );
}
